import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FormField, FormGrid, SelectionList, Text, } from '@dixa/lib-shared-habla';
import { memo, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';
import { customFieldsApi } from 'apis/customFields';
import { HookFormSelectionListSingle } from 'components/hookForm';
import { elevionArrayComparators, elevionBooleanComparators, elevionComparatorLabels, elevionDateComparators, elevionHtzComparators, ElevionLeafFieldID, elevionNumberComparators, elevionStringComparators, elevionTimeComparators, isLeafNode, } from 'utils/elevion';
import { LeafFieldField } from './component/leafFieldField/leafFieldField';
import { LeafValueField } from './component/leafValueField/leafValueField';
const fieldTypeComparatorMap = {
    bfield: elevionBooleanComparators,
    nfield: elevionNumberComparators,
    dfield: elevionDateComparators,
    sfield: elevionStringComparators,
    safield: elevionArrayComparators.filter((comparator) => comparator !== 'contains_all'),
    tfield: elevionTimeComparators,
    htzfield: elevionHtzComparators,
};
export const ElevionConditionLeaf = memo(function ElevionLeafCondition({ index, parentName, }) {
    var _a, _b, _c, _d;
    // TODO: [TEAMS-13693] remove omit after updating to RHF v8
    const { control, setValue } = useFormContext();
    const name = parentName !== undefined && index !== undefined
        ? `${parentName}.${index}`
        : 'visibility_condition';
    const node = useWatch({
        control,
        name,
    });
    const { field: nodeField, field_type: nodeFieldType, value: selectedNodeValue, comparator: selectedNodeComparator, } = node;
    const comparators = (_a = fieldTypeComparatorMap[nodeFieldType]) !== null && _a !== void 0 ? _a : [];
    const shouldBeEmpty = isLeafNode(node) && node.comparator === 'is_empty';
    const response = customFieldsApi.useReadAttributesQuery({
        entityId: 'conversation',
    });
    const customFields = (_d = (_c = (_b = response.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.attributes) !== null && _d !== void 0 ? _d : [];
    const activeCustomFields = customFields.filter((field) => !field.is_deactivated);
    const previousNodeField = usePrevious(nodeField);
    // cascade the change of field value to comparator/field_type
    useEffect(() => {
        if (nodeField === ElevionLeafFieldID.Queue && nodeFieldType !== 'safield') {
            setValue(`${name}.comparator`, elevionArrayComparators[0]);
            setValue(`${name}.field_type`, 'safield');
        }
        if (nodeField === ElevionLeafFieldID.Queue &&
            !Array.isArray(selectedNodeValue)) {
            setValue(`${name}.value`, []);
        }
        if (nodeField && nodeField.startsWith('custom__')) {
            const identifier = nodeField.split('custom__')[1];
            const selectedField = activeCustomFields.find((field) => field.identifier === identifier);
            if (selectedField) {
                const isArrayType = selectedField.type === 'select';
                const isStringType = selectedField.type === 'text';
                const selectedFieldType = isArrayType ? 'safield' : 'sfield';
                const isSupportedComparator = fieldTypeComparatorMap[selectedFieldType].includes(selectedNodeComparator);
                const compatibleComparator = isSupportedComparator
                    ? selectedNodeComparator
                    : isArrayType
                        ? elevionArrayComparators[0]
                        : elevionStringComparators[0];
                if (nodeFieldType !== selectedFieldType) {
                    // Reset field_type if field_type is incompatible
                    setValue(`${name}.field_type`, selectedFieldType);
                }
                if (selectedNodeComparator !== compatibleComparator) {
                    // Reset comparator if comparator is incompatible
                    setValue(`${name}.comparator`, compatibleComparator);
                }
                if (isArrayType) {
                    const shouldReset = 
                    // if value is incompatible type
                    !Array.isArray(selectedNodeValue) ||
                        // if field is changed
                        (previousNodeField && previousNodeField !== nodeField) ||
                        // if value shouldBeEmpty and it is not
                        (Array.isArray(selectedNodeValue) &&
                            shouldBeEmpty &&
                            selectedNodeValue.length > 0);
                    if (shouldReset) {
                        setValue(`${name}.value`, []);
                    }
                }
                else if (isStringType) {
                    const shouldReset = 
                    // if value is incompatible type
                    typeof selectedNodeValue !== 'string' ||
                        // if field is changed
                        (previousNodeField && previousNodeField !== nodeField);
                    if (shouldReset) {
                        setValue(`${name}.value`, '');
                    }
                }
            }
        }
    }, [
        activeCustomFields,
        nodeField,
        nodeFieldType,
        name,
        setValue,
        selectedNodeComparator,
        selectedNodeValue,
        node,
        shouldBeEmpty,
        previousNodeField,
    ]);
    return (_jsx(Box, Object.assign({ margin: "0 0 0 200", width: "100%" }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 2 }, { children: _jsx(LeafFieldField, { parentName: name, customFields: activeCustomFields }) })), _jsx(FormField, Object.assign({ span: 2 }, { children: _jsx(HookFormSelectionListSingle, Object.assign({ name: `${name}.comparator`, control: control, noLabel: true, noAlert: true, componentProps: {
                            getItemTitleByValue: (comparator) => {
                                return (_jsx(Text.Paragraph, { children: elevionComparatorLabels[comparator] }));
                            },
                            placeholder: 'Select a comparator',
                        } }, { children: comparators.map((comparator) => (_jsx(SelectionList.Item, Object.assign({ value: comparator }, { children: elevionComparatorLabels[comparator] }), comparator))) })) })), _jsx(FormField, Object.assign({ span: 2 }, { children: _jsx(LeafValueField, { parentName: name, customFields: activeCustomFields }) }))] }) })));
});
